import { useNavigate } from "react-router-dom";
import { ICasinoGame } from "@hypexone/uicore";

// Wide banners span two of the four desktop columns
const WIDE_PROVIDERS_DESKTOP = ["ROYALGAMING", "VIRTUALCRICKET"];
// Only these go full width on mobile's two-column grid
const WIDE_PROVIDERS_MOBILE = ["ROYALGAMING"];

const normalizeProvider = (name?: string) =>
  (name ?? "").replace(/[^a-z0-9]/gi, "").toUpperCase();

const ProvidersSection = ({
  casinoLobby,
  isLoggedIn,
  showPopUp,
}: {
  casinoLobby: ICasinoGame[];
  isLoggedIn?: boolean;
  showPopUp?: () => void;
}) => {
  const navigate = useNavigate();

  const handleProviderClick = (provider: ICasinoGame) => {
    const {
      gameId,
      gameName,
      gameCode,
      providerName,
      subProviderName,
      superProviderName,
      sectionCategory,
    } = provider ?? {};

    // Rows carrying a gameId are games — launch them rather than filtering the
    // casino page. Rows without one are providers.
    if (gameId) {
      // The game page needs an authed call to fetch its URL, so stop here and
      // prompt to log in rather than landing on a page stuck loading.
      if (!isLoggedIn) {
        showPopUp?.();
        return;
      }

      navigate(
        `/dc/gamev1.1/${gameName?.toLowerCase().replace(/\s+/g, "-")}-${btoa(
          gameId.toString(),
        )}-${btoa(gameCode ?? "")}-${btoa(providerName ?? "")}-${btoa(
          subProviderName ?? "",
        )}-${btoa(superProviderName ?? "")}`,
        { state: { gameName } },
      );
      return;
    }

    navigate({
      pathname: "/casino",
      search: `?subProvider=${encodeURIComponent(
        providerName ?? "",
      )}&category=${encodeURIComponent(sectionCategory || "ALL")}`,
    });
  };

  // Rendered as bare grid cells so they flow into HomePage's tile grid,
  // filling the space beside a half-width tile instead of starting a new grid.
  return (
    <>
      {casinoLobby.map((provider, index) => {
          const name = normalizeProvider(provider.providerName);
          const isWideDesktop = WIDE_PROVIDERS_DESKTOP.includes(name);
          const isWideMobile = WIDE_PROVIDERS_MOBILE.includes(name);

          return (
          <div
            key={`${provider.sectionCode}-${
              provider.gameId ?? provider.providerName
            }-${index}`}
            className={`relative w-full cursor-pointer overflow-hidden ${
              isWideMobile ? "col-span-4" : "col-span-2"
            } ${
              isWideDesktop
                ? "md:col-span-6 md:aspect-[32/10]"
                : "md:col-span-3 md:aspect-[16/10]"
            }`}
            onClick={() => handleProviderClick(provider)}
          >
            <img
              src={
                provider.sectionImage ||
                provider.trendingThumbnail ||
                provider.urlThumb
              }
              alt={provider.gameName || provider.providerName}
              className="w-full h-auto object-cover md:absolute md:inset-0 md:h-full md:w-full"
              loading="lazy"
            />

            <div className="flex w-full h-[32px] sm:h-[24px] flex-shrink-0 absolute bottom-0 left-0 right-0 border-b-4 border-goldenYellow">
              <div className="flex flex-1 items-center bg-gradient-to-r from-black to-transparent text-white pl-2 pr-1 py-1 text-[11px] font-bold min-w-0">
                <span className="truncate">
                  {provider.gameName || provider.providerName}
                </span>
              </div>
              <button
                type="button"
                className="flex items-center justify-center flex-shrink-0 bg-goldenYellow bg-[linear-gradient(110deg,#FFB600_0%,#FFB600_38%,#FFF1B8_50%,#FFB600_62%,#FFB600_100%)] bg-[length:250%_100%] animate-playNow text-[#1A1A1A] px-2 pl-4 py-1 text-[12px] font-extrabold leading-[120%] tracking-[0.02em] [text-shadow:0_1px_0_rgba(255,255,255,0.6)] cursor-pointer min-w-[70px] [clip-path:polygon(15%_0,100%_0,100%_100%,0_100%)] hover:brightness-110"
              >
                Play Now
              </button>
            </div>
          </div>
          );
        })}
    </>
  );
};

export default ProvidersSection;
